// src/pages/ColisEdit.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
  collection,
  getDocs,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";

import UploadField from "../components/UploadField";
import ArticlesEditor from "../components/ArticlesEditor";
import PaymentsEditor from "../components/PaymentsEditor";
import LotsEditor from "../components/LotsEditor";
import ProgressChecklist from "../components/ProgressChecklist";
import {
  computeCoverage,
  computePaymentStatus,
  deriveStatusFromCoverage,
  checkCoverageConsistency,
} from "../utils/tracking";

export default function ColisEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  // --- état du document principal ---
  const [code, setCode] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [origin, setOrigin] = useState("Chine");
  const [destination, setDestination] = useState("Lomé");
  const [estimate, setEstimate] = useState("");
  const [cover, setCover] = useState("");
  const [status, setStatus] = useState("en_attente");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // --- sous-collections en édition ---
  const [articles, setArticles] = useState([]); // chaque item peut contenir {id?, title, qty, unitValueXOF/value, weight, images:[]}
  const [payments, setPayments] = useState([]); // {id?, amount, currency, rate, method, note, date}
  const [lots, setLots] = useState([]); // {id?, step, qty, note, date, images, containerNo?, shippingUrl?}

  // --- chargement initial ---
  useEffect(() => {
    let unsubDoc;
    (async () => {
      const ref = doc(db, "colischine", id);
      // abonnement au doc principal
      unsubDoc = onSnapshot(ref, (snap) => {
        const d = snap.data();
        if (!d) return;
        setCode(d.code || "");
        setClientName(d.clientName || "");
        setClientPhone(d.clientPhone || "");
        setOrigin(d.origin || "Chine");
        setDestination(d.destination || "Lomé");
        setEstimate(String(d.estimateXOF ?? ""));
        setCover(d.cover || "");
        setStatus(d.status || "en_attente");
      });

      // fetch des sous-collections
      const [artSnap, paySnap, lotSnap] = await Promise.all([
        getDocs(collection(db, "colischine", id, "articles")),
        getDocs(collection(db, "colischine", id, "payments")),
        getDocs(collection(db, "colischine", id, "lots")),
      ]);
      setArticles(artSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setPayments(paySnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLots(lotSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    })();
    return () => {
      if (unsubDoc) unsubDoc();
    };
  }, [id]);

  // --- agrégats live (côté UI) ---
  const paidTotalXOF = useMemo(
    () => payments.reduce((s, p) => s + Number(p.amount || 0) * Number(p.rate || 1), 0),
    [payments]
  );
  const balance = useMemo(
    () => Number(estimate || 0) - Number(paidTotalXOF || 0),
    [estimate, paidTotalXOF]
  );
  const coverage = useMemo(() => computeCoverage(articles, lots), [articles, lots]);
  const paymentStatus = useMemo(
    () => computePaymentStatus(estimate, paidTotalXOF),
    [estimate, paidTotalXOF]
  );
  const coverageIssues = useMemo(() => checkCoverageConsistency(coverage), [coverage]);

  // --- helpers: sync sous-collections avec batch ---
  async function syncSubcollection(colisId, sub, nextItems) {
    const colRef = collection(db, "colischine", colisId, sub);
    const snap = await getDocs(colRef);

    const existing = new Map(snap.docs.map((d) => [d.id, d.data()]));
    const nextById = new Map(nextItems.filter((x) => x.id).map((x) => [x.id, x]));

    const batch = writeBatch(db);

    // suppressions
    for (const id of existing.keys()) {
      if (!nextById.has(id)) batch.delete(doc(colRef, id));
    }

    // upserts
    for (const item of nextItems) {
      const { id: itemId, ...data } = item;
      if (itemId && existing.has(itemId)) {
        batch.update(doc(colRef, itemId), data);
      } else if (itemId && !existing.has(itemId)) {
        // si l'UI fournit un id nouveau (rare), on le respecte
        batch.set(doc(colRef, itemId), data);
      } else {
        // création avec id auto
        const newRef = doc(colRef);
        batch.set(newRef, data);
      }
    }

    await batch.commit();
  }

  // --- sauvegarde globale ---
  const saveAll = async () => {
    try {
      setSaving(true);
      // 1) sync sous-collections
      await Promise.all([
        syncSubcollection(id, "articles", articles),
        syncSubcollection(id, "payments", payments),
        syncSubcollection(id, "lots", lots),
      ]);

      // 2) recalculs et statut
      const cov = computeCoverage(articles, lots);
      let nextStatus = deriveStatusFromCoverage(cov);
      if (nextStatus === "recu" && Number(paidTotalXOF) < Number(estimate || 0)) {
        // garde-fou: pas reçu si pas soldé
        nextStatus = status === "recu" ? "recu" : (status || "en_conteneur");
      }

      // 3) MAJ du doc principal
      await updateDoc(doc(db, "colischine", id), {
        code,
        clientName,
        clientPhone,
        origin,
        destination,
        estimateXOF: Number(estimate || 0),
        paidTotalXOF: Number(paidTotalXOF || 0),
        balanceXOF: Number(balance || 0),
        status: nextStatus,
        cover: cover || null,
        updatedAt: serverTimestamp(),
      });

      alert("Modifications enregistrées ✅");
    } catch (e) {
      console.error(e);
      alert("Erreur lors de l'enregistrement des modifications");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center text-muted">Chargement du colis…</div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="mb-0">✏️ Éditer le colis</h4>
          <div className="text-muted">ID: {id}</div>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-light" onClick={() => navigate(-1)}>Retour</button>
          <button className="btn btn-primary" disabled={saving} onClick={saveAll}>
            {saving ? "Enregistrement…" : "Enregistrer les modifications"}
          </button>
        </div>
      </div>

      <div className="row g-3">
        {/* --- Carte infos principales --- */}
        <div className="col-lg-6">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="card-title">Informations colis</h6>
              <div className="row g-2">
                <div className="col-md-4">
                  <label className="form-label">Code suivi</label>
                  <input className="form-control" value={code} onChange={(e) => setCode(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Client</label>
                  <input className="form-control" value={clientName} onChange={(e) => setClientName(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Téléphone</label>
                  <input className="form-control" value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Origine</label>
                  <input className="form-control" value={origin} onChange={(e) => setOrigin(e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Destination</label>
                  <input className="form-control" value={destination} onChange={(e) => setDestination(e.target.value)} />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Estimation (XOF)</label>
                  <input className="form-control" value={estimate} onChange={(e) => setEstimate(e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Image de couverture</label>
                  <UploadField pathPrefix={`colis/covers`} onUploaded={setCover} />
                </div>

                <div className="col-12 d-flex gap-2 align-items-center">
                  {cover && (
                    <img src={cover} alt="cover" className="rounded border" style={{ width: 96, height: 96, objectFit: "cover" }} />
                  )}
                  <div className="small text-muted">
                    Statut actuel : <strong>{status}</strong>
                    <br />
                    Payé: <strong>{paidTotalXOF.toLocaleString("fr-FR")} XOF</strong> — Reste: {" "}
                    <strong>{balance.toLocaleString("fr-FR")} XOF</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- Contrôle & progression --- */}
        <div className="col-lg-6">
          <ProgressChecklist
            cov={coverage}
            paidTotalXOF={paidTotalXOF}
            estimateXOF={estimate}
            payStatus={paymentStatus}
            issues={coverageIssues}
          />
          {coverageIssues.length > 0 && (
            <div className="alert alert-warning">
              <div className="fw-bold mb-1">Vérifications à corriger :</div>
              <ul className="mb-0">
                {coverageIssues.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* --- Éditeurs --- */}
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="card-title mb-3">Articles</h6>
              <ArticlesEditor articles={articles} setArticles={setArticles} />
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="card-title mb-3">Paiements</h6>
              <PaymentsEditor payments={payments} setPayments={setPayments} />
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="card-title mb-3">Étapes (lots)</h6>
              <LotsEditor lots={lots} setLots={setLots} />
            </div>
          </div>
        </div>

        <div className="col-12 d-flex justify-content-end gap-2">
          <button className="btn btn-light" onClick={() => navigate(-1)}>Annuler</button>
          <button className="btn btn-primary" disabled={saving} onClick={saveAll}>
            {saving ? "Enregistrement…" : "Enregistrer les modifications"}
          </button>
        </div>
      </div>
    </div>
  );
}
